import { useCallback, useMemo } from 'react';
import { useGainsightService } from '@mews/framework';

enum CommanderGainsightServiceCustomEventName {
    ApiSuccess = 'Success',
    CheckboxChecked = 'CheckboxChecked',
    ElementHover = 'ElementHover',
    ElementView = 'ElementView',
}

interface ElementHoverData {
    context: 'CustomerNote' | 'NRSAutomation' | 'BillingAutomation';
}

interface CheckboxCheckedData {
    context: 'UpdateRequestedCategory';
    checked: boolean;
}

export type BillingAutomationChangesType = 'added' | 'removed' | 'replaced';
export type BillingAutomationChangesOrigin = 'Rates' | 'TravelAgency' | 'Company' | 'MergeGroup' | 'Account';

type ApiSuccessData =
    | {
          context: 'AssignedCategoryChanged';
      }
    | {
          context: 'BillingAutomationChanged';
          data: {
              changes: BillingAutomationChangesType;
              origin: BillingAutomationChangesOrigin;
          };
      };

export type ElementViewData =
    | {
          context: 'NRSAutomation' | 'BillingAutomation' | 'NewReservationPanel';
      }
    | {
          context: 'BillingAutomationChanges';
          data: {
              changes: BillingAutomationChangesType;
              origin: BillingAutomationChangesOrigin;
          };
      };

// prettier-ignore
type CommanderGainsightEventData<E extends CommanderGainsightServiceCustomEventName = CommanderGainsightServiceCustomEventName> =
    E extends CommanderGainsightServiceCustomEventName.ElementHover ? ElementHoverData :
    E extends CommanderGainsightServiceCustomEventName.CheckboxChecked ? CheckboxCheckedData :
    E extends CommanderGainsightServiceCustomEventName.ApiSuccess ? ApiSuccessData :
    E extends CommanderGainsightServiceCustomEventName.ElementView ? ElementViewData :
    undefined;

/**
 * Will return a list of custom event actions to track them into Gainsight service
 */
export const useGainsightCommanderEvents = () => {
    const gainsightService = useGainsightService();

    const track = useCallback(
        <E extends CommanderGainsightServiceCustomEventName>(event: E, data: CommanderGainsightEventData<E>) => {
            gainsightService.trackEvent(event, data);
        },
        [gainsightService]
    );

    return useMemo(
        () => ({
            onElementHover: (data: ElementHoverData) => {
                track(CommanderGainsightServiceCustomEventName.ElementHover, data);
            },
            onCheckboxChecked: (data: CheckboxCheckedData) => {
                track(CommanderGainsightServiceCustomEventName.CheckboxChecked, data);
            },
            onApiSuccess: (data: ApiSuccessData) => {
                track(CommanderGainsightServiceCustomEventName.ApiSuccess, data);
            },
            onElementView: (data: ElementViewData) => {
                track(CommanderGainsightServiceCustomEventName.ElementView, data);
            },
        }),
        [track]
    );
};
